import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { SelectItem } from '@/components/core/select';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { SelectFormField } from '@/components/forms/select-form-field';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages } from '@/shared/helpers';

// Sentinel for the account-level default option (radix Select forbids empty-string item values).
const ALL_SERVICES_VALUE = 'all-services';

const schema = z.object({
  carrierServiceId: z.string().min(1, 'Choose the carrier service these brackets apply to'),
});

type Props = {
  /** The account's carrier — its services are the addable choices. */
  carrierId?: string;
  /** Service ids already configured (or with an open draft) — excluded from the choices. */
  excludeServiceIds: Set<string>;
  /** Whether the account-level default config can still be added (i.e. it doesn't exist yet). */
  canAddDefault: boolean;
  /** Adds a blank draft config for the choice — null for the account-level default. */
  onSelect: (carrierServiceId: string | null) => void;
};

/**
 * Picks what a new bracket config applies to — a not-yet-configured carrier service, or "all
 * services" (the account-level default). It only chooses the target; the brackets are then entered
 * in the blank draft table that appears. The addable services are derived here from the carrier's
 * service catalogue.
 */
export function AddCarrierServiceDialogContent({
  carrierId,
  excludeServiceIds,
  canAddDefault,
  onSelect,
}: Props) {
  const { shippingGateway } = useGateways();
  const { data: carrierServices = [] } = useQuery({
    queryKey: ['carrierServices'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getCarrierServices({ cursor, take }, { signal }),
      ),
    enabled: !!carrierId,
  });

  const availableServices = carrierServices.filter(
    (service) => service.carrierId === carrierId && !excludeServiceIds.has(service.id),
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { carrierServiceId: '' },
  });

  return (
    <FormDialogContent
      className="sm:max-w-md"
      form={form}
      submitLabel="Add"
      onSubmit={({ carrierServiceId }) =>
        onSelect(carrierServiceId === ALL_SERVICES_VALUE ? null : carrierServiceId)
      }
    >
      <DialogHeader>
        <DialogTitle>Add carrier service</DialogTitle>
        <DialogDescription>
          Choose a carrier service to configure, or set the account default for all services.
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <SelectFormField
          control={form.control}
          name="carrierServiceId"
          placeholder="Select a service"
          className="w-full"
        >
          {canAddDefault && (
            <SelectItem value={ALL_SERVICES_VALUE}>Default (all services)</SelectItem>
          )}
          {availableServices.map((service) => (
            <SelectItem key={service.id} value={service.id}>
              {service.name}
            </SelectItem>
          ))}
        </SelectFormField>
      </DialogBody>
    </FormDialogContent>
  );
}
